'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksYapanDoktorlar() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-yapan-doktorlar-hangi-brans-yetkilidir-kimler-uygulama-yapabilir';
  const pageTitle = 'Botoks Yapan Doktorlar: Hangi Branş Yetkilidir, Kimler Uygulama Yapabilir?';
  const pageDescription = 'Botoks kim tarafından yapılabilir? Hangi doktorlar botoks uygulama yetkisine sahiptir? Plastik cerrah, dermatolog, medikal estetik hekimi farklarıyla kapsamlı rehber.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Yapan Doktorlar', href: '/tr/blog/botoks-yapan-doktorlar-hangi-brans-yetkilidir-kimler-uygulama-yapabilir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks yapan doktorlar ve branş yetkileri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Doktor Seçimi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks Yapan Doktorlar: Hangi Branş Yetkilidir, Kimler Uygulama Yapabilir?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>13 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>16 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks (botulinum toksin), medikal estetikte en sık yapılan enjeksiyonlardan biridir.
                Ancak sosyal medya ve güzellik merkezlerinde bu uygulamanın "herkes tarafından yapılabildiği" gibi yanlış bir algı yayılmış durumda.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Gerçekte, botoks yalnızca yetkili hekimler tarafından uygulanabilir.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-6 mb-6">
                <p className="text-indigo-800 font-medium text-lg">
                  "Botoks bir iğne değil, sinir-kas sistemine müdahaledir."
                </p>
                <p className="text-indigo-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Botoks Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, sinir uçlarındaki asetilkolin salınımını durdurarak kasın geçici olarak gevşemesini sağlar.
                Bu nedenle hem estetik hem de nörolojik alanlarda kullanılır:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Kırışıklık tedavisi</li>
                  <li>Migren</li>
                  <li>Diş sıkma (bruksizm)</li>
                  <li>Koltuk altı terlemesi (hiperhidroz)</li>
                  <li>Yüz asimetrisi düzeltmeleri</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  Bu etkiler ancak kas anatomisini bilen bir hekim tarafından güvenli şekilde yönetilebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Kimler Botoks Yapabilir?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-indigo-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Branş</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yetkili mi?</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Plastik, Rekonstrüktif ve Estetik Cerrah</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz anatomisini en iyi bilen branş</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dermatoloji (Cildiye) Uzmanı</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt ve kas etkileşiminde deneyimli</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Medikal Estetik Hekimi (sertifikalı)</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Tıp mezunu olup ek botoks eğitimi almış hekim</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nöroloji Uzmanı</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Medikal botoks (migren, spastisite) alanında yetkilidir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Diş Hekimi</td>
                      <td className="border border-gray-300 px-4 py-3 text-yellow-600 font-medium">🔸 Sınırlı</td>
                      <td className="border border-gray-300 px-4 py-3">Sadece masseter (çene kası) alanında uygulama yapabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hemşire / Estetisyen / Kozmetolog</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Tıbbi enjeksiyon yetkisi yoktur</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Sonuç: Botoks yalnızca tıp doktorları tarafından yapılabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Yasal Dayanak
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-green-900 mb-4">📜 1219 Sayılı Tababet Kanunu</h4>
                <p className="text-green-800 italic">
                  "Her türlü ilaç ve enjeksiyon uygulaması, yalnızca tıp doktoru veya hekim gözetiminde yapılabilir."
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-4">📜 Sağlık Bakanlığı Yönetmeliği (2024)</h4>
                <p className="text-blue-800 italic">
                  "Botulinum toksin uygulamaları yalnızca hekimler tarafından, onaylı ürünlerle, klinik koşullarda yapılmalıdır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Hangi Doktor Size Uygun?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Hedef</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Tercih Edilen Branş</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yüz estetiği, kırışıklık</td>
                      <td className="border border-gray-300 px-4 py-3">Plastik cerrah veya dermatolog</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Terleme tedavisi</td>
                      <td className="border border-gray-300 px-4 py-3">Medikal estetik hekimi</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Migren veya kas spazmı</td>
                      <td className="border border-gray-300 px-4 py-3">Nöroloji uzmanı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Diş sıkma (bruksizm)</td>
                      <td className="border border-gray-300 px-4 py-3">Diş hekimi (sınırlı alan)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Her branşın uygulama sınırları farklıdır; ancak hekim olma şartı değişmez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Eğitim Farkı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks eğitimi, medikal estetik alanında Tıp Fakültesi mezunu doktorlara özel verilmekte.
                Bu eğitimlerde:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Kas anatomisi</li>
                  <li>Sinir yapısı</li>
                  <li>Doz hesaplaması</li>
                  <li>Komplikasyon yönetimi</li>
                </ul>
                <p className="text-yellow-800 mt-4">
                  konularında pratik ve teorik bilgi kazandırılır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Hekim Olmayan Kişiler Neden Riskli?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yanlış enjeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Kas asimetrisi ve göz kapağı düşüklüğü</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Fazla doz</td>
                      <td className="border border-gray-300 px-4 py-3">Donuk yüz ifadesi</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sterilite eksikliği</td>
                      <td className="border border-gray-300 px-4 py-3">Enfeksiyon</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sahte ürün</td>
                      <td className="border border-gray-300 px-4 py-3">Alerji ve etki kaybı</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  "Botoksu yanlış yere yapmak, estetik değil, nörolojik bir hata olur."
                </p>
                <p className="text-red-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Botoks Uygulayıcısı Seçerken Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Doktor unvanını doğrula (Sağlık Bakanlığı Hekim Bilgi Bankası'ndan)</li>
                  <li>Muayenehane Sağlık Bakanlığı onaylı mı kontrol et</li>
                  <li>Kullanılan botoks markasını sor (Allergan, Dysport, Xeomin vb.)</li>
                  <li>Steril ortamda işlem yapıldığından emin ol</li>
                  <li>Onam formu imzalamadan işlem yaptırma</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Uygulamada Fark Yaratan Noktalar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Profesyonel Hekim Yaklaşımı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Doz ayarı</td>
                      <td className="border border-gray-300 px-4 py-3">Kas kuvvetine göre bireysel planlama</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nokta seçimi</td>
                      <td className="border border-gray-300 px-4 py-3">Anatomik varyasyonlara göre belirlenir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Estetik denge</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz ifadeleri korunur</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sonuç takibi</td>
                      <td className="border border-gray-300 px-4 py-3">2 hafta sonra kontrol yapılır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Etik Açıdan Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik tıp, medikal etik kurallara bağlıdır.
                Hekim olmayan birinin botoks yapması, tıbbi etik ve yasal sorumluluk ihlalidir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  "Estetikte etik, enjeksiyondan daha önemlidir."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Botoksu diş hekimi yapabilir mi?</h3>
                  <p className="text-gray-600">Sadece çene kası (masseter) bölgesinde.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Güzellik merkezinde botoks güvenli mi?</h3>
                  <p className="text-gray-600">Hayır, yasal değildir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Botoksu hemşire uygulayabilir mi?</h3>
                  <p className="text-gray-600">Hayır, yalnızca hekim uygulayabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Hekimimi nasıl doğrularım?</h3>
                  <p className="text-gray-600">Sağlık Bakanlığı'nın "Hekim Bilgi Bankası" üzerinden sorgulama yapılabilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Botoks işlemi ne kadar sürer?</h3>
                  <p className="text-gray-600">Uzman hekim tarafından yapıldığında 10-20 dakika arası sürer.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Hangi botoks markaları güvenli?</h3>
                  <p className="text-gray-600">Allergan (Botox), Ipsen (Dysport), Merz (Xeomin) gibi Sağlık Bakanlığı onaylı markalar güvenlidir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Yasal Kaynaklar
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>1219 Sayılı Tababet Kanunu</li>
                  <li>Sağlık Bakanlığı Yönetmeliği, 2024</li>
                  <li>TETD (2024) – "Botulinum Toksin Uygulama Rehberi"</li>
                  <li>ISAPS (2023) – "Qualified Injector Guidelines"</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks uygulaması, uzmanlık ve anatomi bilgisi gerektiren ciddi bir tıbbi işlemdir.
                Hekim olmayan kişiler tarafından yapılması, hem yasal hem biyolojik olarak risklidir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Botoks bir markanın değil, bir doktorun işidir.
                  Kimin eline teslim olduğun, sonucunu belirler."
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#yasaluygulama</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#estetikdoktor</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#sağlıkgüvenliği</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Güvenli Botoks Uygulaması İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan güvenli ve yasal botoks uygulaması hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Güvenli botoks uygulaması hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/guzellik-merkezlerinin-botoks-yapma-yetkisi-yasal-durum-ve-klinik-gercekler"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Güzellik Merkezlerinin Botoks Yapma Yetkisi</h4>
              <p className="text-gray-600 text-sm">Yasal durum ve klinik gerçekler.</p>
            </a>
            <a
              href="/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Botoksu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Güvenli ürün nasıl tespit edilir?</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}